import React, { useState } from "react";
import { DateTime } from "luxon";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper";

const downloadCSV = (data, filename) => {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map(row => headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(","))
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function StatisticsModal({
  isOpen,
  loading,
  selectedRow,
  statisticsData,
  incidentCounts,
  postmortemPreventiveMeasures = [],
  onClose,
}) {
  const [expandedIncidents, setExpandedIncidents] = useState(false);

  if (!isOpen) return null;

  const calculateDuration = (start, end) => {
    if (!start || !end) return "N/A";
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);
    if (!startDate.isValid || !endDate.isValid) return "N/A";
    return `${Math.ceil(endDate.diff(startDate, "days").days)} days`;
  };

  const calculateProgress = (start, end) => {
    if (!start || !end) return 0;
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);
    if (!startDate.isValid || !endDate.isValid) return 0;
    const totalDays = Math.ceil(endDate.diff(startDate, "days").days);
    const daysPassed = Math.ceil(DateTime.now().diff(startDate, "days").days);
    return totalDays > 0 ? Math.min(100, Math.round((daysPassed / totalDays) * 100)) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? date.toFormat("d/M/yyyy") : "N/A";
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? Math.ceil(DateTime.now().diff(date, "days").days) : "N/A";
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = DateTime.fromISO(dateStr);
    return date.isValid ? Math.max(0, Math.ceil(date.diff(DateTime.now(), "days").days)) : "N/A";
  };

  const formatIncidentDate = (dateStr) => {
    if (!dateStr) return "N/A";
    // Handle format: "2025-10-13 11:03:40" or ISO format
    let date;
    if (dateStr.includes(" ")) {
      // Parse "YYYY-MM-DD HH:mm:ss" format
      const [datePart] = dateStr.split(" ");
      date = DateTime.fromISO(datePart);
    } else {
      date = DateTime.fromISO(dateStr);
    }
    return date.isValid ? date.toFormat("dd-MM-yyyy") : "N/A";
  };

  const handleDownloadStatistics = () => {
    if (!selectedRow) return;

    // Build CSV content manually for better formatting
    let csvContent = "Statistics Report\n\n";
    
    // Application Information Section
    csvContent += "APPLICATION INFORMATION\n";
    csvContent += `App ID,${selectedRow.app_id || "N/A"}\n`;
    csvContent += `App Name,${selectedRow.app_name || "N/A"}\n`;
    csvContent += `Incident,${selectedRow.incident || "N/A"}\n`;
    csvContent += `Status,${selectedRow.status ? `${selectedRow.status} Channel` : "N/A"}\n\n`;
    
    // Timeline Section
    csvContent += "TIMELINE\n";
    csvContent += `Incident Date,${formatDate(selectedRow._originalData?.added_to_red_channel)}\n`;
    csvContent += `Final Review Date,${formatDate(selectedRow._originalData?.final_review)}\n`;
    csvContent += `Implementation Duration,${calculateDuration(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}\n`;
    csvContent += `Progress (%),${calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}\n`;
    csvContent += `Days Since Incident,${daysSince(selectedRow._originalData?.added_to_red_channel)}\n`;
    csvContent += `Days Until Review,${daysUntil(selectedRow._originalData?.final_review)}\n\n`;
    
    // Change Implementation Section
    csvContent += "CHANGE IMPLEMENTATION STATISTICS\n";
    csvContent += `Total Changes,${statisticsData?.total || 0}\n`;
    csvContent += `Changes Implemented Successfully,${statisticsData?.successful || 0}\n`;
    csvContent += `Success Rate (%),${statisticsData?.successful_percent ? statisticsData.successful_percent.toFixed(1) : "0"}\n\n`;
    
    // Incident Details Section
    csvContent += "INCIDENT DETAILS (LAST 12 MONTHS)\n";
    csvContent += `P1/P2 Incidents,${incidentCounts?.p1p2_count || 0}\n`;
    csvContent += `P3/P4 Incidents,${incidentCounts?.p3p4_count || 0}\n`;
    csvContent += `Total Incidents,${incidentCounts?.total || 0}\n\n`;
    
    // P1/P2 Incident Details
    if (incidentCounts?.p1p2 && incidentCounts.p1p2.length > 0) {
      csvContent += "P1/P2 INCIDENT DETAILS\n";
      csvContent += "Incident ID,Affected CI,Opened At,Short Description\n";
      incidentCounts.p1p2.forEach((incident) => {
        const id = incident.id || "N/A";
        const affectedCi = (incident.affected_ci || "N/A").replace(/,/g, ";");
        const openedAt = formatIncidentDate(incident.opened_at);
        const description = (incident.short_description || "N/A").replace(/,/g, ";").replace(/"/g, '""').replace(/\n/g, " ");
        csvContent += `${id},${affectedCi},${openedAt},"${description}"\n`;
      });
      csvContent += "\n";
    }
    
    // P3/P4 Incident Details
    if (incidentCounts?.p3p4 && incidentCounts.p3p4.length > 0) {
      csvContent += "P3/P4 INCIDENT DETAILS\n";
      csvContent += "Incident ID,Affected CI,Opened At,Short Description\n";
      incidentCounts.p3p4.forEach((incident) => {
        const id = incident.id || "N/A";
        const affectedCi = (incident.affected_ci || "N/A").replace(/,/g, ";");
        const openedAt = formatIncidentDate(incident.opened_at);
        const description = (incident.short_description || "N/A").replace(/,/g, ";").replace(/"/g, '""').replace(/\n/g, " ");
        csvContent += `${id},${affectedCi},${openedAt},"${description}"\n`;
      });
      csvContent += "\n";
    }
    
    // Preventive Measures Section
    if (postmortemPreventiveMeasures && postmortemPreventiveMeasures.length > 0) {
      csvContent += "POSTMORTEM PREVENTIVE MEASURES\n";
      csvContent += "Type,ID,Status,Assignee,Due Date,Description\n";
      postmortemPreventiveMeasures.forEach((measure) => {
        const type = measure.type || "N/A";
        const id = type === "jira" ? (measure.jiraid || "N/A") : type === "ptask" ? (measure.ptaskid || "N/A") : "N/A";
        const status = (measure.status || "N/A").replace(/,/g, ";");
        const assignee = (measure.assignee || "N/A").replace(/,/g, ";");
        const dueDate = measure.due_date ? formatDate(measure.due_date) : "N/A";
        const description = (measure.description || "No description provided").replace(/,/g, ";").replace(/\n/g, " ");
        csvContent += `${type},${id},${status},${assignee},${dueDate},"${description}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const filename = `Statistics_${selectedRow.app_id}_${selectedRow.incident}_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <McModal
      heading="Statistics"
      dimension="large"
      padding="default"
      width="50%"
      open={isOpen}
      closed={onClose}
      closing={onClose}
      zindex={999}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <mc-loading-indicator></mc-loading-indicator>
        </div>
      ) : selectedRow ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Header */}
          <div style={{ display: "flex", gap: "16px", alignItems: "center", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
            <div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                App ID: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.app_id}</span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                App Name: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.app_name}</span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>
                Incident: <span style={{ fontWeight: "600", color: "#141414" }}>{selectedRow.incident}</span>
              </div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <mc-tag appearance={selectedRow.status === "Red" ? "error" : "success"} fit="small">
                {selectedRow.status} Channel
              </mc-tag>
            </div>
          </div>

          {/* Change Implementation Duration */}
          <div style={{ padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Change Implementation Duration
            </h3>
            <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Incident Date</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {formatDate(selectedRow._originalData?.added_to_red_channel)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Final Review</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {formatDate(selectedRow._originalData?.final_review)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Implementation Duration</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                  {calculateDuration(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}
                </div>
              </div>
            </div>
            <div style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "0.875rem", color: "#666" }}>
                  Progress: {calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%
                </span>
              </div>
              <div style={{ width: "100%", height: "8px", backgroundColor: "#b3e5fc", borderRadius: "4px", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${calculateProgress(selectedRow._originalData?.added_to_red_channel, selectedRow._originalData?.final_review)}%`,
                    height: "100%",
                    backgroundColor: "#0277bd",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Success Rate */}
          <div style={{ padding: "20px", backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
              Success Rate of Change Implementation
            </h3>
            {statisticsData ? (
              <>
                <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Total Changes</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#141414" }}>{statisticsData.total || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Implemented Successfully</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#2e7d32" }}>{statisticsData.successful || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "4px" }}>Success Rate</div>
                    <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#2e7d32" }}>
                      {statisticsData.successful_percent ? `${statisticsData.successful_percent.toFixed(1)}%` : "0%"}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "16px" }}>
                  <div style={{ width: "100%", height: "8px", backgroundColor: "#c8e6c9", borderRadius: "4px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${statisticsData.successful_percent || 0}%`,
                        height: "100%",
                        backgroundColor: "#388e3c",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: "1rem" }}>No statistics data available</div>
            )}
          </div>

          {/* Incident Details */}
          <div style={{ padding: "20px", backgroundColor: "#fff3e0", borderRadius: "8px", border: "1px solid #ffe0b2" }}>
            <div style={{ marginBottom: "16px" }}>
              <h3 style={{ margin: "0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
                Incident Details for {selectedRow?.app_name || "Application"} (Last 12 Months)
              </h3>
            </div>
            {incidentCounts ? (
              <>
                <div style={{ display: "flex", gap: "24px", justifyContent: "space-between", marginBottom: "16px" }}>
                  <div style={{ textAlign: "center", padding: "12px 24px", backgroundColor: "white", borderRadius: "8px", minWidth: "120px" }}>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>P1/P2 Incidents</div>
                    <mc-tag appearance="error" fit="small" style={{ marginBottom: "8px" }}>
                      {incidentCounts.p1p2_count || 0}
                    </mc-tag>
                    <div style={{ fontSize: "0.75rem", color: "#d32f2f", fontWeight: "600" }}>Critical & High Priority</div>
                  </div>
                  <div style={{ textAlign: "center", padding: "12px 24px", backgroundColor: "white", borderRadius: "8px", minWidth: "120px" }}>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>P3/P4 Incidents</div>
                    <mc-tag appearance="warning" fit="small" style={{ marginBottom: "8px" }}>
                      {incidentCounts.p3p4_count || 0}
                    </mc-tag>
                    <div style={{ fontSize: "0.75rem", color: "#f57c00", fontWeight: "600" }}>Medium & Low Priority</div>
                  </div>
                  <div style={{ textAlign: "center", padding: "12px 24px", backgroundColor: "white", borderRadius: "8px", minWidth: "120px" }}>
                    <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Total Incidents</div>
                    <div style={{ fontSize: "2rem", fontWeight: "700", color: "#141414" }}>{incidentCounts.total || 0}</div>
                  </div>
                </div>
                
                {(incidentCounts?.p1p2?.length > 0 || incidentCounts?.p3p4?.length > 0) && (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: expandedIncidents ? "20px" : "0" }}>
                    <McButton 
                      appearance="primary"
                      variant="plain"
                      label='Details'
                      trailingIcon={expandedIncidents ? "chevron-up" : "chevron-down"}
                      fit="small" 
                      onClick={() => setExpandedIncidents(!expandedIncidents)}
                    >
            
                    </McButton>
                  </div>
                )}
              
                {/* Expanded Incident Details Table */}
                {expandedIncidents && (
                  <div style={{ marginTop: "20px" }}>
                    {/* P1/P2 Incidents */}
                    {incidentCounts.p1p2 && incidentCounts.p1p2.length > 0 && (
                      <div style={{ marginBottom: "24px" }}>
                        <h4 style={{ margin: "0 0 12px 0", fontSize: "0.875rem", fontWeight: "600", color: "#d32f2f" }}>
                          P1/P2 Incidents ({incidentCounts.p1p2.length})
                        </h4>
                        <div style={{ backgroundColor: "white", borderRadius: "8px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#f5f5f5" }}>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "25%" }}>Incident ID</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "25%" }}>Affected CI</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "25%" }}>Opened At</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "25%" }}>Short Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {incidentCounts.p1p2.map((incident, index) => (
                                <tr key={index} style={{ borderBottom: index < incidentCounts.p1p2.length - 1 ? "1px solid #e0e0e0" : "none" }}>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>
                                    <mc-tag appearance="error" fit="small">{incident.id}</mc-tag>
                                  </td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>{incident.affected_ci || "N/A"}</td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>{formatIncidentDate(incident.opened_at)}</td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414", lineHeight: "1.4" }}>{incident.short_description || "N/A"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* P3/P4 Incidents */}
                    {incidentCounts.p3p4 && incidentCounts.p3p4.length > 0 && (
                      <div>
                        <h4 style={{ margin: "0 0 12px 0", fontSize: "0.875rem", fontWeight: "600", color: "#f57c00" }}>
                          P3/P4 Incidents ({incidentCounts.p3p4.length})
                        </h4>
                        <div style={{ backgroundColor: "white", borderRadius: "8px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#f5f5f5" }}>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "15%" }}>Incident ID</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "20%" }}>Affected CI</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "15%" }}>Opened At</th>
                                <th style={{ padding: "12px", textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#666", borderBottom: "1px solid #e0e0e0", width: "50%" }}>Short Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {incidentCounts.p3p4.map((incident, index) => (
                                <tr key={index} style={{ borderBottom: index < incidentCounts.p3p4.length - 1 ? "1px solid #e0e0e0" : "none" }}>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>
                                    <mc-tag appearance="warning" fit="small">{incident.id}</mc-tag>
                                  </td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>{incident.affected_ci || "N/A"}</td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414" }}>{formatIncidentDate(incident.opened_at)}</td>
                                  <td style={{ padding: "12px", fontSize: "0.813rem", color: "#141414", lineHeight: "1.4" }}>{incident.short_description || "N/A"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: "1rem" }}>No incident data available</div>
            )}
          </div>

          {/* Days Since/Until */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={{ padding: "16px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Days since incident</div>
              <div style={{ fontSize: "2rem", fontWeight: "600", color: "#0077b2" }}>
                {daysSince(selectedRow._originalData?.added_to_red_channel)}
              </div>
            </div>
            <div style={{ padding: "16px", backgroundColor: "#f3e5f5", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "8px" }}>Days until review</div>
              <div style={{ fontSize: "2rem", fontWeight: "600", color: "#6a1b9a" }}>
                {daysUntil(selectedRow._originalData?.final_review)}
              </div>
            </div>
          </div>

          {/* Postmortem Preventive Measures */}
          {postmortemPreventiveMeasures && postmortemPreventiveMeasures.length > 0 && (
            <div style={{ padding: "20px", backgroundColor: "#f3e5f5", borderRadius: "8px" }}>
              <h3 style={{ margin: "0 0 16px 0", fontSize: "1rem", fontWeight: "600", color: "#141414" }}>
                Postmortem Preventive Measures
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {postmortemPreventiveMeasures.map((measure, index) => {
                  const isJira = measure.type === "jira";
                  const isPTask = measure.type === "ptask";
                  const itemId = isJira ? measure.jiraid : isPTask ? measure.ptaskid : "N/A";
                  const itemLabel = isJira ? "JIRA ID" : isPTask ? "PTASK ID" : "ID";
                  
                  return (
                    <div 
                      key={index}
                      style={{ 
                        padding: "16px", 
                        backgroundColor: "white", 
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0"
                      }}
                    >
                      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", marginBottom: "12px", flexWrap: "wrap" }}>
                        <div style={{ flex: "0 0 auto" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>{itemLabel}</div>
                          <mc-tag appearance="neutral" fit="small">
                            {itemId || "N/A"}
                          </mc-tag>
                        </div>
                        <div style={{ flex: "0 0 auto" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Status</div>
                          <mc-tag 
                            appearance={
                              measure.status?.toLowerCase() === "done" ? "success" : 
                              measure.status?.toLowerCase() === "in progress" ? "warning" : 
                              "neutral"
                            } 
                            fit="small"
                          >
                            {measure.status || "N/A"}
                          </mc-tag>
                        </div>
                        {measure.due_date && (
                          <div style={{ flex: "0 0 auto" }}>
                            <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Due Date</div>
                            <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                              {formatDate(measure.due_date)}
                            </div>
                          </div>
                        )}
                        <div style={{ flex: "1", marginLeft: "auto", textAlign: "right", minWidth: "150px" }}>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Assignee</div>
                          <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#141414" }}>
                            {measure.assignee || "-"}
                          </div>
                        </div>
                      </div>
                      {measure.description && (
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "#666", marginBottom: "4px" }}>Description</div>
                          <div style={{ fontSize: "0.875rem", color: "#141414", lineHeight: "1.5" }}>
                            {measure.description || "No description provided"}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>No data available</div>
      )}

      <McButton trailingicon="tray-arrow-down" slot="secondaryAction" appearance="neutral" fit="medium" click={handleDownloadStatistics} disabled={loading || !selectedRow}>
        <span slot="leadingicon">
          <mc-icon icon="tray-arrow-down"></mc-icon>
        </span>
        Download
      </McButton>
      <McButton slot="primaryAction" appearance="primary" fit="medium" click={onClose}>
        Close
      </McButton>
    </McModal>
  );
}
